import { HubConnection } from '@microsoft/signalr';
import AppContext from 'App/AppContext';
import { IPSpinner, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { useEditorCommandBridge } from 'components/Copilot/Panel/Runtime/useEditorCommandBridge';
import RichTextEditorMCE from 'components/Text/RichTextEditorMCE';
import { globalStackTokensGapSmall } from 'globalStyles';
import { CopilotConversationContentBlock } from 'models/copilot/copilotConversation';
import { capSelectionText, CopilotDocumentSelection } from 'models/copilot/copilotDocumentSelection';
import ResourceLink from 'models/resourceLink';
import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetLink } from 'services/Api/linkService';
import { apiRequest, GraphScopeId } from 'services/Auth/authConfig';
import {
  getTextWebParts,
  getValidPageContent,
  graphGetSitePage,
  graphUpdateSitePageTextWebParts,
} from 'services/Graph/graphServicePage';
import { ISitePage, ITextWebPart } from 'services/Graph/SharepointInterfaces';
import logger from 'services/Logging/logService';
import { CopilotAgentEvent, CopilotAgentEventKind } from 'services/SignalR/CopilotHub';
import AppError from 'utils/appError';
import { sanitizeHtml } from 'utils/html';
import { cloneObject } from 'utils/object';
import SharePointPageRenderer from '../PageRenderer/SharePointPageRenderer';
import { SharePointTextWebPart } from '../PageRenderer/WebParts/SharePointTextWebPart';
import './SharePointPageEditor.css';

export type SharePointPageSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface SharePointPageEditorHandle {
  // Concatenated HTML of the editable web parts, used to update the session content block on run completion.
  getPageHtml: () => string;
  // Persist every web part edited (and buffered) during an agent run to SharePoint in a single pass. Returns
  // the outcome so the host can surface a save status. A no-op (ok: true) when nothing is dirty.
  flushPendingEdits: () => Promise<{ ok: boolean; error?: string }>;
}

interface ISharePointPageEditorProps {
  block?: CopilotConversationContentBlock;
  hubConnection?: HubConnection;
  sessionId?: number;
  // While true, an agent run is in progress: web part edits are buffered locally and not written to SharePoint
  // until the run completes (flushPendingEdits), so intermediate passes do not create SharePoint page versions.
  isAgentWorking?: boolean;
  // Reports a web part commit to SharePoint (on blur or agent edit) so the host can show a save indicator. On
  // failure the status is 'error' and errorMessage carries a human-readable reason.
  onSaveStateChange?: (status: SharePointPageSaveStatus, errorMessage?: string) => void;
  // Reports the current text selection (or null when nothing is selected) so the host can show the Copilot
  // selection chip and attach the highlight to the request. Mirrors the docx editors' onSelectionChange.
  onSelectionChange?: (selection: CopilotDocumentSelection | null) => void;
}

interface IPageContext {
  siteId: string;
  pageId: string;
  altTenantId?: string;
}

const PAGE_EDITOR_TOOL_NAMES = new Set<string>(['list_webparts', 'read_webpart', 'update_webpart']);
type ApplyWebPartHtmlResult = 'notFound' | 'unchanged' | 'changed';

const collectTextWebPartHtml = (page: ISitePage | undefined): string => {
  if (!page) return '';

  return getTextWebParts(page)
    .map((webPart) => webPart.innerHtml ?? '')
    .join('\n');
};

const findTextWebPart = (page: ISitePage | undefined, webPartId: string): ITextWebPart | undefined => {
  if (!page) return undefined;

  return getTextWebParts(page).find((webPart) => webPart.id === webPartId);
};

const hasDirtyTextWebParts = (page: ISitePage | undefined): boolean => {
  if (!page) return false;

  return getTextWebParts(page).some((webPart) => webPart.updated === true);
};

interface IEditableTextWebPartProps {
  webPart: ITextWebPart;
  page: ISitePage;
  siteId?: string;
  isEditing: boolean;
  onStartEdit: () => void;
  // hasChanged is false when the editor gives back exactly what it loaded (a blur without a real edit), so the
  // host can skip the commit instead of writing TinyMCE's re-serialization of unchanged content to SharePoint.
  onCommit: (html: string, hasChanged: boolean) => void;
  // Reports the plain-text selection inside this web part's editor (TinyMCE runs in an iframe, so the
  // page-level selectionchange listener cannot see it; this covers the actively-edited web part).
  onSelectionText?: (selectedText: string) => void;
}

const EditableTextWebPart = (props: IEditableTextWebPartProps): JSX.Element => {
  const { t } = useTranslation(['copilot']);
  // Hold the in-progress edit in state (not a ref). RichTextEditorMCE runs TinyMCE in controlled mode
  // (value={html}) and re-renders itself on every change (it tracks the character count); controlled TinyMCE
  // resets its content to the html prop on re-render. A ref-backed value would leave html stale at the
  // original text, so the first keystroke would be reverted ~200ms later. Keeping html in sync with the typed
  // content avoids the reset.
  const [draft, setDraft] = useState<string>(props.webPart.innerHtml ?? '');

  // Re-seed the draft from the (possibly agent-edited) web part whenever we are not editing, so opening the
  // editor always starts from the current content. Guarded by !isEditing so it never clobbers in-progress typing.
  useEffect(() => {
    if (!props.isEditing) setDraft(props.webPart.innerHtml ?? '');
  }, [props.isEditing, props.webPart.innerHtml]);

  if (props.isEditing) {
    return (
      <div className="copilotPageEditor-webPart editing">
        <RichTextEditorMCE
          html={draft}
          toolbarOnTop={true}
          onChange={(value) => setDraft(value)}
          onBlur={(lastValue, hasChanged) => props.onCommit(lastValue ?? draft, hasChanged)}
          onSelectionChange={props.onSelectionText}
          hideRemainingChars={true}
          disallowFullscreen={true}
        />
      </div>
    );
  }

  return (
    <div
      className="copilotPageEditor-webPart editable"
      role="button"
      tabIndex={0}
      onClick={props.onStartEdit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') props.onStartEdit();
      }}
    >
      <span className="copilotPageEditor-webPart-hint">{t('copilot:Chat.Document.PageEditHint')}</span>
      <SharePointTextWebPart webPart={props.webPart} pageContent={props.page} siteId={props.siteId} />
    </div>
  );
};

// Editable SharePoint page surface for the Copilot panel. Text web parts can be edited inline (click to open
// a rich-text editor, blur to save the web part to SharePoint via the Graph API). Agent page tools are bridged
// through SignalR: the backend asks to list/read/update web parts and this component answers and applies them.
// Rendering, content validation (getValidPageContent) and section theming are delegated to SharePointPageRenderer
// via its editableTextWebPart render-prop, so the editor does not fork the renderer's traversal.
const SharePointPageEditor = forwardRef<SharePointPageEditorHandle, ISharePointPageEditorProps>(
  function SharePointPageEditor(props, ref) {
    const appContext = useContext(AppContext);
    const { t } = useTranslation(['copilot']);
    const [page, setPage] = useState<ISitePage | undefined>(undefined);
    const [link, setLink] = useState<ResourceLink | undefined>(undefined);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [hasError, setHasError] = useState<boolean>(false);
    const [editingWebPartId, setEditingWebPartId] = useState<string | undefined>(undefined);
    const pageContextRef = useRef<IPageContext | undefined>(undefined);
    const pageRef = useRef<ISitePage | undefined>(undefined);
    // Single-flight guard so a user-blur persist cannot overlap the agent run-completion flush.
    const isPersistingRef = useRef<boolean>(false);
    const pendingPersistRef = useRef<boolean>(false);
    const persistPromiseRef = useRef<Promise<{ ok: boolean; error?: string }> | undefined>(undefined);
    // Wraps the rendered page so the selection listener can scope to selections made inside the editor.
    const editorContainerRef = useRef<HTMLDivElement | null>(null);

    const linkId = props.block?.savedLibraryLinkId;
    const siteId = pageContextRef.current?.siteId;
    const { onSaveStateChange, onSelectionChange } = props;
    // Latest onSelectionChange in a ref so the document-level selection listener (attached once) always calls
    // the current callback without re-subscribing.
    const onSelectionChangeRef = useRef(onSelectionChange);
    onSelectionChangeRef.current = onSelectionChange;

    // Report a non-empty selected-text value as a normalized selection. Empty/whitespace is ignored (never
    // emitted) so the host keeps the last non-empty selection when focus leaves the page (e.g. the user clicks
    // into the chat input), matching the docx editors' behavior.
    const reportSelectionText = useCallback((selectedText: string) => {
      const text = selectedText?.trim();
      if (!text) return;

      onSelectionChangeRef.current?.({ text: capSelectionText(selectedText) });
    }, []);

    // Capture selections made on the static (rendered, non-editing) web parts via the DOM Selection API,
    // scoped to this editor's container. The actively-edited web part runs TinyMCE in an iframe that this
    // listener cannot see; that surface is covered by EditableTextWebPart's onSelectionText instead.
    useEffect(() => {
      if (!onSelectionChange) return undefined;

      const handler = () => {
        const selection = window.getSelection();
        if (!selection || selection.isCollapsed || selection.rangeCount === 0) return;

        const container = editorContainerRef.current;
        const anchorNode = selection.anchorNode;
        if (!container || !anchorNode || !container.contains(anchorNode)) return;

        reportSelectionText(selection.toString());
      };

      document.addEventListener('selectionchange', handler);

      return () => document.removeEventListener('selectionchange', handler);
    }, [onSelectionChange, reportSelectionText]);

    const setPageState = useCallback((next: ISitePage | undefined) => {
      pageRef.current = next;
      setPage(next);
    }, []);

    // Resolve the library link and load the SharePoint page. Validate the page the same way the renderer does
    // (getValidPageContent) so the editor and the read-only renderer agree on which content is shown.
    useEffect(() => {
      let cancelled = false;

      const loadPage = async () => {
        if (!linkId) {
          setIsLoading(false);
          setHasError(true);

          return;
        }

        setIsLoading(true);
        setHasError(false);
        try {
          const accessToken = await appContext.getAccessToken(apiRequest.scopes);
          const resolvedLink = await apiGetLink(linkId, accessToken, appContext.globalDataCache);
          if (!resolvedLink?.list?.spSiteId || !resolvedLink.pageId) {
            throw new Error('The library page could not be resolved.');
          }

          const graphInterface = await appContext.getGraphInterface(
            GraphScopeId.SiteOwner,
            resolvedLink.list.altTenantId,
          );
          const loadedPage = await graphGetSitePage(
            graphInterface.client,
            resolvedLink.list.spSiteId,
            resolvedLink.pageId,
            true,
          );
          if (cancelled) return;

          pageContextRef.current = {
            siteId: resolvedLink.list.spSiteId,
            pageId: resolvedLink.pageId,
            altTenantId: resolvedLink.list.altTenantId,
          };
          setLink(resolvedLink);
          setPageState(getValidPageContent(cloneObject(loadedPage)) ?? loadedPage);
        } catch (err) {
          logger.debug('SharePointPageEditor: failed to load page', err);
          if (!cancelled) setHasError(true);
        } finally {
          if (!cancelled) setIsLoading(false);
        }
      };

      void loadPage();

      return () => {
        cancelled = true;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linkId, setPageState]);

    // Apply a single web part edit to local state only (no SharePoint write). The HTML is sanitized first: it
    // can be authored by the AI agent (update_webpart) or pasted by the user, and is both rendered and later
    // PATCHed back to SharePoint, so scripts / event handlers must be stripped. The web part is marked dirty
    // (updated = true) and stays dirty until persistDirtyWebParts succeeds. setPageState writes pageRef
    // synchronously, so a subsequent agent read_webpart/list_webparts sees the buffered edit immediately.
    const applyWebPartHtml = useCallback(
      (webPartId: string, html: string): ApplyWebPartHtmlResult => {
        const current = pageRef.current;
        if (!current) return 'notFound';

        const currentTarget = findTextWebPart(current, webPartId);
        if (!currentTarget) return 'notFound';

        const sanitizedHtml = sanitizeHtml(html);
        if (sanitizeHtml(currentTarget.innerHtml ?? '') === sanitizedHtml) {
          return currentTarget.updated === true ? 'changed' : 'unchanged';
        }

        const updatedPage = cloneObject(current) as ISitePage;
        const target = findTextWebPart(updatedPage, webPartId);
        if (!target) return 'notFound';

        target.innerHtml = sanitizedHtml;
        target.updated = true;
        if (isPersistingRef.current) pendingPersistRef.current = true;
        setPageState(updatedPage);

        return 'changed';
      },
      [setPageState],
    );

    // Persist every web part edited (and buffered) so far to SharePoint in a single pass.
    // graphUpdateSitePageTextWebParts only PATCHes parts with updated === true, so this flushes the whole dirty
    // set at once. Dirty flags are cleared only on success so a failed flush is retried. Single-flighted so a
    // user-blur persist cannot overlap the agent run-completion flush.
    const persistDirtyWebParts = useCallback(async (): Promise<{ ok: boolean; error?: string }> => {
      if (isPersistingRef.current) {
        pendingPersistRef.current = true;

        return persistPromiseRef.current ?? { ok: true };
      }

      const persistDirtyWebPartsOnce = async (): Promise<{ ok: boolean; error?: string }> => {
        const context = pageContextRef.current;
        const current = pageRef.current;
        if (!context || !current) return { ok: false };

        const updatedPage = cloneObject(current) as ISitePage;
        const dirty = getTextWebParts(updatedPage).filter((webPart) => webPart.updated === true);
        if (dirty.length === 0) return { ok: true };

        try {
          const graphInterface = await appContext.getGraphInterface(GraphScopeId.SiteOwner, context.altTenantId);
          await graphUpdateSitePageTextWebParts(graphInterface.client, context.siteId, context.pageId, updatedPage);

          const nextPage = cloneObject(pageRef.current ?? updatedPage) as ISitePage;
          dirty.forEach((savedWebPart) => {
            if (!savedWebPart.id) return;

            const currentWebPart = findTextWebPart(nextPage, savedWebPart.id);
            if (currentWebPart?.updated === true && currentWebPart.innerHtml === savedWebPart.innerHtml) {
              currentWebPart.updated = false;
            }
          });
          setPageState(nextPage);

          return { ok: true };
        } catch (err) {
          logger.debug('SharePointPageEditor: failed to update web parts', err);

          return { ok: false, error: AppError.fromApiError(err).message };
        }
      };

      isPersistingRef.current = true;
      const persistLoop = (async (): Promise<{ ok: boolean; error?: string }> => {
        try {
          let outcome: { ok: boolean; error?: string } = { ok: true };
          do {
            pendingPersistRef.current = false;
            outcome = await persistDirtyWebPartsOnce();
            if (!outcome.ok) return outcome;
          } while (pendingPersistRef.current || hasDirtyTextWebParts(pageRef.current));

          return outcome;
        } finally {
          isPersistingRef.current = false;
        }
      })();

      persistPromiseRef.current = persistLoop;
      try {
        return await persistLoop;
      } finally {
        persistPromiseRef.current = undefined;
      }
    }, [appContext, setPageState]);

    const flushPendingEdits = useCallback(() => persistDirtyWebParts(), [persistDirtyWebParts]);

    useImperativeHandle(
      ref,
      (): SharePointPageEditorHandle => ({
        getPageHtml: () => collectTextWebPartHtml(pageRef.current),
        flushPendingEdits,
      }),
      [flushPendingEdits],
    );

    // User finished editing a web part inline. Apply the edit, and when no agent run is in progress persist it
    // to SharePoint immediately (driving the save indicator). During an agent run the edit is buffered and the
    // run-completion flush persists it.
    const onCommitEdit = useCallback(
      (webPartId: string, html: string, hasChanged: boolean) => {
        setEditingWebPartId(undefined);
        // A blur without a real edit must not write anything: TinyMCE re-serializes the HTML it loaded, so
        // committing that back marks the web part dirty and creates a SharePoint page version for nothing.
        if (!hasChanged) return;

        if (applyWebPartHtml(webPartId, html) !== 'changed') return;

        if (props.isAgentWorking) return;

        onSaveStateChange?.('saving');
        void persistDirtyWebParts().then((outcome) => {
          if (outcome.ok) onSaveStateChange?.('saved');
          else onSaveStateChange?.('error', outcome.error);
        });
      },
      [applyWebPartHtml, persistDirtyWebParts, props.isAgentWorking, onSaveStateChange],
    );

    const isPageEditorCommand = useCallback((event: CopilotAgentEvent): boolean => {
      return event.kind === CopilotAgentEventKind.EditorToolCommand && PAGE_EDITOR_TOOL_NAMES.has(event.toolName ?? '');
    }, []);

    const executePageEditorCommand = useCallback(
      (_event: CopilotAgentEvent, args: Record<string, unknown>): unknown => {
        const currentPage = pageRef.current;
        switch (_event.toolName) {
          case 'list_webparts':
            return {
              webParts: (currentPage ? getTextWebParts(currentPage) : []).map((webPart) => ({
                id: webPart.id,
                html: webPart.innerHtml ?? '',
              })),
            };
          case 'read_webpart': {
            const webPart = findTextWebPart(currentPage, String(args.webPartId ?? ''));

            return webPart ? { id: webPart.id, html: webPart.innerHtml ?? '' } : { error: 'Web part not found.' };
          }
          case 'update_webpart': {
            const webPartId = String(args.webPartId ?? '');
            // Buffer the edit locally; the run-completion flush persists it to SharePoint in a single pass so
            // intermediate agent passes do not create SharePoint page versions.
            const result = applyWebPartHtml(webPartId, String(args.html ?? ''));

            return result === 'notFound' ? { error: 'Web part could not be updated.' } : { ok: true };
          }
          default:
            return { error: `Unknown page command '${_event.toolName ?? ''}'.` };
        }
      },
      [applyWebPartHtml],
    );

    useEditorCommandBridge({
      hubConnection: props.hubConnection,
      isCommand: isPageEditorCommand,
      executeCommand: executePageEditorCommand,
      logPrefix: 'SharePointPageEditor',
    });

    if (isLoading) {
      return (
        <IPStack verticalFill verticalAlign="center" horizontalAlign="center" tokens={globalStackTokensGapSmall}>
          <IPSpinner label={t('copilot:Chat.Document.EditorLoading')} />
        </IPStack>
      );
    }

    if (hasError || !page || !link) {
      return (
        <IPStack verticalFill verticalAlign="center" horizontalAlign="center" tokens={globalStackTokensGapSmall}>
          <IPText>{t('copilot:Chat.Document.EditorError')}</IPText>
        </IPStack>
      );
    }

    return (
      <div ref={editorContainerRef} style={{ height: '100%' }}>
        <SharePointPageRenderer
          link={link}
          page={page}
          siteId={siteId}
          hideHeader
          disableScrollContainer
          editableTextWebPart={(webPart, rendererPage) => (
            <EditableTextWebPart
              webPart={webPart}
              page={rendererPage}
              siteId={siteId}
              isEditing={editingWebPartId === webPart.id}
              onStartEdit={() => setEditingWebPartId(webPart.id)}
              onCommit={(html, hasChanged) => onCommitEdit(webPart.id ?? '', html, hasChanged)}
              onSelectionText={reportSelectionText}
            />
          )}
        />
      </div>
    );
  },
);

export default SharePointPageEditor;
